import * as validators from '@vuelidate/validators';
import { isValidIBAN } from 'ibantools';
import { helpers, maxLength, minLength, minValue } from '@vuelidate/validators';
import type { ValidationRule } from '@vuelidate/core';

const validateBsn: ValidationRule = (value: string) => {
  if (!value || value.length === 0) {
    return true;
  }

  if (value === '00000000000' || value.length !== 9) {
    return false;
  }

  const [a, b, c, d, e, f, g, h, i] = Array.from(value, (char) => parseInt(char, 10));
  const result = 9 * a + 8 * b + 7 * c + 6 * d + 5 * e + 4 * f + 3 * g + 2 * h + -1 * i;

  return result > 0 && result % 11 === 0;
};

export const bsn: ValidationRule = validators.helpers.withMessage(
  'Het BSN nummer is ongeldig, deze moet uit 9 cijfers bestaan en een geldig formaat hebben.',
  validateBsn,
);

export const RSIN: ValidationRule = validators.helpers.withMessage(
  'Het RSIN nummer is ongeldig, deze moet uit 9 cijfers bestaan en een geldig formaat hebben.',
  validateBsn,
);

export const kvkValidation: ValidationRule = helpers.withMessage(
  `Het KvK-nummer bestaat uit 8 cijfers.`,
  (value: string) => /^[0-9]{8}$/.test(value)
);

export const alphaNumeric = (conditionFn: () => boolean) => helpers.withMessage(
  'Dit veld mag alleen bestaan uit letters en cijfers',
  (value: string) => {
    if (conditionFn()) return true;
    return /^[a-zA-Z0-9]+$/.test(value);
  },
);

export const iban: ValidationRule = validators.helpers.withMessage(
  'IBAN is niet geldig.',
  (value: string) => isValidIBAN(value.replace(/\s/g, '')),
);

export const postcode: ValidationRule = validators.helpers.withMessage(
  'De postcode moet volgens het formaat 1234 AB zijn.',
  (val: string, siblings) => {
    if (siblings.land !== 'NL') return true;
    return helpers.regex(/^[1-9][0-9]{3} [A-Z]{2}$/)(val);
  },
);

export const pastDate: ValidationRule = validators.helpers.withMessage(
  'Dit veld mag niet in de toekomst zijn.',
  validators.maxValue(Date.now()),
);

export const futureDate: ValidationRule = validators.helpers.withMessage(
  'Dit veld mag niet in het verleden zijn.',
  validators.minValue(Date.now()),
);

export const adultDate: ValidationRule = validators.helpers.withMessage(
  'U moet minimaal 18 jaar zijn.',
  validators.maxValue(new Date().setFullYear(new Date().getFullYear() - 18)),
);

export const childDate: ValidationRule = validators.helpers.withMessage(
  'Het minderjarige kind mag niet ouder zijn dan 18 jaar.',
  validators.minValue(new Date().setFullYear(new Date().getFullYear() - 18)),
);

export const email: ValidationRule = validators.helpers.withMessage(
  'Dit veld moet een geldig e-mailadres bevatten.',
  validators.email,
);

export const required: ValidationRule = validators.helpers.withMessage(
  'Dit veld is verplicht.',
  validators.required,
);

export const requiredCheckbox: ValidationRule = validators.helpers.withMessage(
  'Dit veld is verplicht.',
  validators.sameAs(true),
);

export const integer: ValidationRule = validators.helpers.withMessage(
  'Dit veld moet een geheel getal zijn.',
  validators.integer,
);

export const passwordGenericValidator = {
  required: required,
  minLength: helpers.withMessage('Uw wachtwoord moet uit minimaal 8 karakters bestaan.', minLength(8)),
  maxLength: helpers.withMessage('Uw wachtwoord mag niet uit meer dan 15 karakters bestaan.', maxLength(15)),
  uppercase: helpers.withMessage('Uw wachtwoord moet tenminste 1 hoofdletter bevatten.', helpers.regex(/[A-Z]/)),
  lowercase: helpers.withMessage('Uw wachtwoord moet tenminste 1 kleine letter bevatten.', helpers.regex(/[a-z]/)),
  number: helpers.withMessage('Uw wachtwoord moet tenminste 1 cijfer bevatten.', helpers.regex(/[0-9]/)),
  special: helpers.withMessage('Uw wachtwoord moet tenminste 1 van deze bijzondere tekens bevatten: !@#$%', helpers.regex(/[!@#$%]/)),
  onlyAllowedChars: helpers.withMessage('Uw wachtwoord mag alleen letters, cijfers en deze bijzondere tekens bevatten: !@#$%', helpers.regex(/^[A-Za-z0-9!@#$%]+$/)),
};

export const nameValidator: ValidationRule = validators.helpers.withMessage(
  "Dit veld mag alleen bestaan uit letters, spaties, apostrof (') en koppeltekens (-).",
  validators.helpers.regex(
    /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u,
  ),
);

export const postcodeCharacterValidation: ValidationRule = validators.helpers.withMessage(
  'Dit veld mag alleen bestaan uit letters, cijfers, punten en spaties.',
  validators.helpers.regex(/^[a-zA-Z0-9 .]*$/),
);

export const initialenValidator: ValidationRule = validators.helpers.withMessage(
  'Dit veld mag alleen bestaan uit letters, spaties en punten.',
  validators.helpers.regex(/^[a-zA-Z .]*$/),
);


export const adultDateSynvest = helpers.withMessage(
  "U moet minimaal 18 jaar zijn.",
  (value: string) => {
    if (!value) return true;

    const today = new Date();
    const birthDate = new Date(value);
    const eighteenYearsAgo = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate(),
    );

    return birthDate <= eighteenYearsAgo;
  },
);

export const futureDateSynvest = helpers.withMessage(
  "Dit veld mag niet in het verleden zijn.",
  (value: string) => {
    if (!value) return true;

    const today = new Date();
    const inputDate = new Date(value);

    return inputDate >= today;
  },
);